import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/ui/password-input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, User, Mail, Lock, Upload } from "lucide-react";
import { z } from "zod";
import { validatePassword, PASSWORD_REQUIREMENTS } from "@/lib/security";
import { PasswordStrengthIndicator } from "@/components/PasswordStrengthIndicator";

const profileSchema = z.object({
  display_name: z.string().trim().min(1, "Display name is required").max(100, "Display name too long"),
  headline: z.string().trim().max(120, "Headline is too long").optional(),
  bio: z.string().trim().max(500, "Bio must be under 500 characters").optional(),
  location: z.string().trim().max(120, "Location is too long").optional(),
});

const passwordSchema = z.object({
  newPassword: z.string().min(PASSWORD_REQUIREMENTS.minLength, `Password must be at least ${PASSWORD_REQUIREMENTS.minLength} characters`),
  confirmPassword: z.string(),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

export default function ProfileSettings() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { toast } = useToast();
  
  const [displayName, setDisplayName] = useState("");
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [headline, setHeadline] = useState("");
  const [bio, setBio] = useState("");
  const [location, setLocation] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [profileErrors, setProfileErrors] = useState<Partial<Record<keyof z.infer<typeof profileSchema>, string>>>({});
  const [passwordErrors, setPasswordErrors] = useState<Partial<Record<keyof z.infer<typeof passwordSchema>, string>>>({});

  const loadProfile = useCallback(async () => {
    if (!user) return;

    setEmail(user.email || "");

    const { data, error } = await supabase
      .from("profiles")
      .select("display_name, avatar_url")
      .eq("user_id", user.id)
      .maybeSingle();

    if (error && error.code !== "PGRST116") {
      console.error("Error loading profile:", error);
      toast({
        title: "Could not load profile",
        description: error.message || "Please try again.",
        variant: "destructive",
      });
      return;
    }

    if (data) {
      setDisplayName(data.display_name);
      setAvatarUrl(data.avatar_url);
      setHeadline(user.user_metadata?.headline || "");
      setBio(user.user_metadata?.bio || "");
      setLocation(user.user_metadata?.location || "");
      return;
    }

    const fallbackName =
      user.user_metadata?.display_name || user.email?.split("@")[0] || "User";

    const { data: newProfile, error: upsertError } = await supabase
      .from("profiles")
      .upsert(
        {
          user_id: user.id,
          display_name: fallbackName,
          avatar_url: user.user_metadata?.avatar_url || null,
        },
        { onConflict: "user_id" }
      )
      .select("display_name, avatar_url")
      .single();

    if (upsertError) {
      console.error("Error creating missing profile:", upsertError);
      return;
    }

    setDisplayName(newProfile.display_name);
    setAvatarUrl(newProfile.avatar_url);
    setHeadline(user.user_metadata?.headline || "");
    setBio(user.user_metadata?.bio || "");
    setLocation(user.user_metadata?.location || "");
  }, [toast, user]);

  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      navigate("/auth");
      return;
    }

    loadProfile();
  }, [authLoading, user, navigate, loadProfile]);

  const syncAuthMetadata = async (data: {
    display_name?: string;
    avatar_url?: string | null;
    headline?: string;
    bio?: string;
    location?: string;
  }) => {
    if (!user) return;

    const { error } = await supabase.auth.updateUser({
      data: {
        ...user.user_metadata,
        ...data,
      },
    });

    if (error) {
      console.error("Error syncing auth metadata:", error);
      toast({
        title: "Warning",
        description: "Profile saved, but we could not refresh your session metadata.",
        variant: "destructive",
      });
    }
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    
    let parsedProfile: z.infer<typeof profileSchema>;
    try {
      parsedProfile = profileSchema.parse({ display_name: displayName, headline, bio, location });
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          title: "Validation Error",
          description: error.errors[0].message,
          variant: "destructive",
        });
        return;
      }
    }

    if (!parsedProfile) return;

    setLoading(true);

    try {
      const { error } = await supabase
        .from("profiles")
        .upsert(
          { user_id: user!.id, display_name: parsedProfile.display_name, avatar_url: avatarUrl },
          { onConflict: "user_id" }
        );

      if (error) throw error;

      await syncAuthMetadata({
        display_name: parsedProfile.display_name,
        avatar_url: avatarUrl,
        headline: parsedProfile.headline,
        bio: parsedProfile.bio,
        location: parsedProfile.location,
      });

      const { error: refreshError } = await supabase.auth.refreshSession();
      if (refreshError) {
        console.warn("Session refresh failed after profile update:", refreshError);
      }

      await loadProfile();

      toast({
        title: "Profile updated",
        description: "Your profile details have been updated successfully.",
      });
    } catch (error: any) {
      console.error("Error updating profile:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to update profile.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate password strength first
    const passwordValidation = validatePassword(newPassword);
    if (!passwordValidation.isValid) {
      toast({
        title: "Weak Password",
        description: passwordValidation.errors[0],
        variant: "destructive",
      });
      return;
    }

    const validation = passwordSchema.safeParse({ newPassword, confirmPassword });

    if (!validation.success) {
      const formattedErrors = validation.error.flatten().fieldErrors;
      setPasswordErrors({
        newPassword: formattedErrors.newPassword?.[0],
        confirmPassword: formattedErrors.confirmPassword?.[0],
      });
      toast({
        title: "Validation Error",
        description: validation.error.errors[0].message,
        variant: "destructive",
      });
      return;
    }

    setPasswordErrors({});

    setLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;

      toast({
        title: "Password updated",
        description: "Your password has been changed successfully.",
      });

      setNewPassword("");
      setConfirmPassword("");
    } catch (error: any) {
      console.error("Error updating password:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to update password.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Avatar must be less than 2MB.",
        variant: "destructive",
      });
      return;
    }

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast({
        title: "Invalid file type",
        description: "Please upload an image file.",
        variant: "destructive",
      });
      return;
    }

    setUploading(true);

    try {
      const fileExt = file.name.split(".").pop();
      const fileName = `${user.id}-${Date.now()}.${fileExt}`;
      const filePath = `${fileName}`;

      // Upload to guest-avatars bucket (public)
      const { error: uploadError } = await supabase.storage
        .from("guest-avatars")
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from("guest-avatars")
        .getPublicUrl(filePath);

      // Update profile with avatar URL
      const { error: updateError } = await supabase
        .from("profiles")
        .update({ avatar_url: publicUrl })
        .eq("user_id", user.id);

      if (updateError) throw updateError;

      setAvatarUrl(publicUrl);

      await syncAuthMetadata({ avatar_url: publicUrl });

      toast({
        title: "Avatar updated",
        description: "Your profile picture has been updated successfully.",
      });
    } catch (error: any) {
      console.error("Error uploading avatar:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to upload avatar.",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const getInitials = () => {
    if (!displayName) return "U";
    return displayName
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="min-h-screen bg-muted/30">
      <div className="container mx-auto max-w-5xl px-4 py-10">
        <div className="flex items-center gap-4 mb-8">
          <Button variant="outline" size="icon" onClick={() => navigate(-1)} className="rounded-full">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <p className="text-sm text-muted-foreground">Account</p>
            <h1 className="text-3xl font-semibold leading-tight">Profile Settings</h1>
            <p className="text-muted-foreground">Update your profile information with live validation and preview.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card className="shadow-sm border-muted">
              <CardHeader className="space-y-1">
                <CardTitle className="flex items-center gap-2 text-xl">
                  <User className="h-5 w-5 text-primary" />
                  Profile Information
                </CardTitle>
                <CardDescription>
                  Keep your details current. We validate inputs before saving and show a preview alongside.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleUpdateProfile} className="space-y-6">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4 rounded-lg border bg-muted/40 p-4">
                    <Avatar className="h-16 w-16 ring-2 ring-background">
                      <AvatarImage src={avatarUrl || undefined} alt={displayName} />
                      <AvatarFallback className="text-lg font-semibold">{getInitials()}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 space-y-2">
                      <Label htmlFor="avatar">Profile Photo</Label>
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                        <Input
                          id="avatar"
                          type="file"
                          accept="image/*"
                          onChange={handleAvatarUpload}
                          disabled={uploading}
                          className="max-w-xs"
                        />
                        <div className="flex gap-2">
                          <Button variant="outline" type="button" onClick={() => setAvatarUrl(null)} disabled={uploading}>
                            Remove
                          </Button>
                          <Button type="button" variant="secondary" disabled={uploading} className="bg-primary/10 text-primary">
                            {uploading ? "Uploading..." : "Upload a Photo"}
                          </Button>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground flex items-center gap-2">
                        <Upload className="h-4 w-4" />
                        JPG, PNG, or GIF up to 2MB.
                      </p>
                    </div>
                  </div>

                  <Separator />

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="displayName">Display Name</Label>
                      <Input
                        id="displayName"
                        type="text"
                        value={displayName}
                        onChange={(e) => {
                          setDisplayName(e.target.value);
                          setProfileErrors((prev) => ({ ...prev, display_name: undefined }));
                        }}
                        placeholder="Enter your display name"
                        maxLength={100}
                        aria-invalid={!!profileErrors.display_name}
                      />
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <p>Use your preferred full name.</p>
                        <span>{displayName.length}/100</span>
                      </div>
                      {profileErrors.display_name && (
                        <p className="text-xs text-destructive">{profileErrors.display_name}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="flex items-center gap-2">
                        <Mail className="h-4 w-4" />
                        Email
                      </Label>
                      <Input id="email" type="email" value={email} disabled className="bg-muted" />
                      <p className="text-xs text-muted-foreground">Email is managed by your account admin.</p>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="headline">Headline</Label>
                      <Input
                        id="headline"
                        type="text"
                        value={headline}
                        onChange={(e) => {
                          setHeadline(e.target.value);
                          setProfileErrors((prev) => ({ ...prev, headline: undefined }));
                        }}
                        placeholder="Tell people what you do"
                        maxLength={120}
                        aria-invalid={!!profileErrors.headline}
                      />
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <p>Visible to meeting participants.</p>
                        <span>{headline.length}/120</span>
                      </div>
                      {profileErrors.headline && (
                        <p className="text-xs text-destructive">{profileErrors.headline}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="location">Location</Label>
                      <Input
                        id="location"
                        type="text"
                        value={location}
                        onChange={(e) => {
                          setLocation(e.target.value);
                          setProfileErrors((prev) => ({ ...prev, location: undefined }));
                        }}
                        placeholder="City, Country"
                        maxLength={120}
                        aria-invalid={!!profileErrors.location}
                      />
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <p>Optional, helps teammates know your time zone.</p>
                        <span>{location.length}/120</span>
                      </div>
                      {profileErrors.location && (
                        <p className="text-xs text-destructive">{profileErrors.location}</p>
                      )}
                    </div>
                  </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="headline">Headline</Label>
                    <Input
                      id="headline"
                      type="text"
                      value={headline}
                      onChange={(e) => setHeadline(e.target.value)}
                      placeholder="Tell people what you do"
                      maxLength={120}
                    />
                    <p className="text-xs text-muted-foreground">Visible to meeting participants.</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="location">Location</Label>
                    <Input
                      id="location"
                      type="text"
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                      placeholder="City, Country"
                      maxLength={120}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="bio">Bio</Label>
                  <Textarea
                    id="bio"
                    value={bio}
                    onChange={(e) => setBio(e.target.value)}
                    placeholder="Share a short description to introduce yourself"
                    rows={4}
                    maxLength={500}
                  />
                  <p className="text-xs text-muted-foreground">Helps teammates recognize you in meetings.</p>
                </div>

                <Button type="submit" disabled={loading}>
                  {loading ? "Updating..." : "Update Profile"}
                </Button>
              </form>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Profile Preview</CardTitle>
              <CardDescription>See how your profile appears to meeting participants</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col sm:flex-row sm:items-center gap-4">
              <Avatar className="h-20 w-20">
                <AvatarImage src={avatarUrl || undefined} alt={displayName} />
                <AvatarFallback className="text-xl">{getInitials()}</AvatarFallback>
              </Avatar>
              <div className="space-y-1">
                <h3 className="text-lg font-semibold leading-tight">{displayName || "Your name"}</h3>
                <p className="text-sm text-muted-foreground">{email || "Email will appear here"}</p>
                {headline && <p className="text-sm font-medium">{headline}</p>}
                {(location || bio) && (
                  <div className="text-sm text-muted-foreground space-y-1">
                    {location && <p>{location}</p>}
                    {bio && <p className="line-clamp-2">{bio}</p>}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Change Password */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="h-5 w-5" />
                Change Password
              </CardTitle>
              <CardDescription>
                Update your password to keep your account secure
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleUpdatePassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="newPassword">New Password</Label>
                  <PasswordInput
                    id="newPassword"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter your new password"
                  />
                  <PasswordStrengthIndicator password={newPassword} />
                </div>

                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <Button type="submit" disabled={loading} className="sm:w-auto w-full">
                      {loading ? "Updating..." : "Update Profile"}
                    </Button>
                    <p className="text-xs text-muted-foreground">Profile changes save to your account and show in preview.</p>
                  </div>
                </form>
              </CardContent>
            </Card>

            <Card className="shadow-sm border-muted">
              <CardHeader>
                <CardTitle className="text-xl flex items-center gap-2">
                  <Lock className="h-5 w-5 text-primary" />
                  Change Password
                </CardTitle>
                <CardDescription>Keep your account secure with a strong password.</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleUpdatePassword} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="newPassword">New Password</Label>
                    <PasswordInput
                      id="newPassword"
                      value={newPassword}
                      onChange={(e) => {
                        setNewPassword(e.target.value);
                        setPasswordErrors((prev) => ({ ...prev, newPassword: undefined }));
                      }}
                      placeholder="Enter your new password"
                      aria-invalid={!!passwordErrors.newPassword}
                    />
                    <PasswordStrengthIndicator password={newPassword} />
                    {passwordErrors.newPassword && (
                      <p className="text-xs text-destructive">{passwordErrors.newPassword}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm New Password</Label>
                    <PasswordInput
                      id="confirmPassword"
                      value={confirmPassword}
                      onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        setPasswordErrors((prev) => ({ ...prev, confirmPassword: undefined }));
                      }}
                      placeholder="Confirm new password"
                      aria-invalid={!!passwordErrors.confirmPassword}
                    />
                    {passwordErrors.confirmPassword && (
                      <p className="text-xs text-destructive">{passwordErrors.confirmPassword}</p>
                    )}
                  </div>

                  <Button type="submit" disabled={loading} className="sm:w-auto w-full">
                    {loading ? "Updating..." : "Update Password"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-4">
            <Card className="sticky top-6 shadow-sm border-muted">
              <CardHeader>
                <CardTitle className="text-xl">Profile Preview</CardTitle>
                <CardDescription>See how your profile appears to others.</CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col sm:flex-row sm:items-center gap-4">
                <Avatar className="h-20 w-20">
                  <AvatarImage src={avatarUrl || undefined} alt={displayName} />
                  <AvatarFallback className="text-xl">{getInitials()}</AvatarFallback>
                </Avatar>
                <div className="space-y-1">
                  <h3 className="text-lg font-semibold leading-tight">{displayName || "Your name"}</h3>
                  <p className="text-sm text-muted-foreground">{email || "Email will appear here"}</p>
                  {headline && <p className="text-sm font-medium">{headline}</p>}
                  {(location || bio) && (
                    <div className="text-sm text-muted-foreground space-y-1">
                      {location && <p>{location}</p>}
                      {bio && <p className="line-clamp-2">{bio}</p>}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
